
import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import PropTypes from "prop-types";
import { fetchWorkcellMonthlyPlannedVsDone } from "../api";
import { formatHoursMinutes } from "../utils/time";
import { CircularProgress, Box } from "@mui/material";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const COLORS = ["#1976d2", "#ff9800", "#43a047"];

const WorkcellMonthlyPlannedVsDoneChart = ({ date, workcellId, onClick, activeIndex, onHover, loading: loadingProp, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  useEffect(() => {
    setLoading(true);
    const callParent = (typeof onLoadingChange === 'function');
    callParent && onLoadingChange(true);
    setError(null);
    const startDate = date;
    fetchWorkcellMonthlyPlannedVsDone(startDate, workcellId)
      .then((result) => {
        const days = getMonthDays(startDate);
        const fullRangeData = days.map((day) => ({
          day,
          planned_work_done: 0,
          planned_not_done: 0,
          unplanned_work_done: 0,
        }));
        result.forEach((entry) => {
          const idx = fullRangeData.findIndex((d) => d.day === entry.day);
          if (idx !== -1) {
            fullRangeData[idx] = { ...fullRangeData[idx], ...entry };
          }
        });
        setData(fullRangeData);
        setLoading(false);
        callParent && onLoadingChange(false);
      })
      .catch((err) => {
        setError(t("common.failedToLoadData"));
        setLoading(false);
        callParent && onLoadingChange(false);
      });
  }, [date, workcellId]);

  const isLoading = typeof loadingProp === 'boolean' ? loadingProp : loading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div>{error}</div>;

  const handleBarClick = (data, index) => {
    if (onClick && data && data.activeLabel) {
      onClick(data.activeLabel);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const plannedWorkDone = payload.find((p) => p.dataKey === "planned_work_done");
      const plannedNotDone = payload.find((p) => p.dataKey === "planned_not_done");
      const unplannedWorkDone = payload.find((p) => p.dataKey === "unplanned_work_done");
      return (
        <div className="custom-tooltip" style={{ background: "#fff", border: "1px solid #ccc", padding: 8 }}>
          <div>
            <b>{t("common.date")}:</b> {label}
          </div>
          {plannedWorkDone && (
            <div style={{ color: plannedWorkDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.plannedDone")}: {formatHoursMinutes(Number(plannedWorkDone.value))}
            </div>
          )}
          {plannedNotDone && (
            <div style={{ color: plannedNotDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.plannedNotDone")}: {formatHoursMinutes(Number(plannedNotDone.value))}
            </div>
          )}
          {unplannedWorkDone && (
            <div style={{ color: unplannedWorkDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.unplannedDone")}: {formatHoursMinutes(Number(unplannedWorkDone.value))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const date = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
          {date.toLocaleString(locale, { month: 'long' })}
        </text>
      </g>
    ) : null;
  };

  return (
    <div style={{ width: "100%", height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
          onClick={handleBarClick}
          syncId="workcellCharts"
        >
          {/* Main X axis: days */}
          <XAxis
            dataKey="day"
            tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
            height={30}
            axisLine={true}
            tickLine={true}
          />
          {/* Secondary X axis: month name */}
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            height={20}
            xAxisId="month"
            tick={MonthTick}
            interval={0}
            allowDataOverflow={true}
            orientation="bottom"
          />
          <YAxis label={{ value: t("workcell.monthlyOverview.plannedVsDone.axisLabel"), angle: -90, position: "insideLeft" }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="planned_work_done" stackId="a" fill={COLORS[0]} name={t("workcell.monthlyOverview.plannedVsDone.legend.plannedDone")} />
          <Bar dataKey="planned_not_done" stackId="a" fill={COLORS[1]} name={t("workcell.monthlyOverview.plannedVsDone.legend.plannedNotDone")} />
          <Bar dataKey="unplanned_work_done" stackId="a" fill={COLORS[2]} name={t("workcell.monthlyOverview.plannedVsDone.legend.unplannedDone")} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

WorkcellMonthlyPlannedVsDoneChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
  loading: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

export default WorkcellMonthlyPlannedVsDoneChart;
